import DOMPurify from 'dompurify';
import { useMemo } from 'react';

// target is allowed through, and the API's own sanitizer strips rel from
// stored rich text, so new-tab links would otherwise lose their opener guard.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

interface SanitizedHtmlProps {
  html: string;
  className?: string;
  as?: 'div' | 'p';
}

/**
 * Renders trusted rich text content (translation strings, CMS copy) after
 * sanitizing it with DOMPurify. Prefer this component over raw
 * dangerouslySetInnerHTML whenever HTML rendering is genuinely required
 * (CH-3389). For untrusted plain text with line breaks, use
 * renderTextWithLineBreaks from shared/util instead.
 */
export function SanitizedHtml({
  html,
  className,
  as: Tag = 'div',
}: SanitizedHtmlProps): JSX.Element {
  const sanitized = useMemo(
    () =>
      DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ADD_ATTR: ['target'],
      }),
    [html]
  );

  // Single audited sink, the content is sanitized above.
  // eslint-disable-next-line react/no-danger
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: sanitized }} />;
}
